/**
 * ResetPasswordPage Component
 *
 * Second step of password reset flow. User receives email with reset link that includes a token.
 * This page validates the token and allows user to set a new password.
 *
 * Flow:
 * 1. User clicks password reset link in email (includes token in URL)
 * 2. This page validates the token
 * 3. User enters new password and confirms
 * 4. Backend updates password and invalidates all existing sessions
 * 5. Redirected to login page
 *
 * URL Format: /reset-password?token=ABC123...
 *
 * Usage:
 *   <Route path="/reset-password" element={<ResetPasswordPage />} />
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { AuthShell, AuthTitle, AuthField, AuthSubmit, AuthNote, authLink } from '@/components/auth/AuthShell';
import { apiClient } from '@/services/api';

type ResetPasswordState = 'validating' | 'form' | 'submitting' | 'success' | 'error' | 'invalid-token';

interface FormErrors {
  password?: string;
  confirmPassword?: string;
  general?: string;
}

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState<ResetPasswordState>('validating');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setState('invalid-token');
      return;
    }

    // Token is present, proceed to form
    setState('form');
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    if (!token) {
      setErrors({ general: 'Invalid or missing reset token' });
      return;
    }

    try {
      setLoading(true);
      setState('submitting');

      // Call backend to reset password
      await apiClient.post('/api/v1/auth/reset-password', {
        token: token,
        password: formData.password,
      });

      // Success - show confirmation message
      setState('success');
      setLoading(false);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Password reset successfully! Please login with your new password.',
          },
          replace: true,
        });
      }, 3000);
    } catch (err: any) {
      setLoading(false);

      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to reset password';

      // Check if token is expired or invalid
      if (err.response?.status === 410 || errorMessage.includes('expired')) {
        setState('invalid-token');
        setErrors({ general: 'Password reset link has expired. Please request a new one.' });
      } else {
        setState('form');
        setErrors({ general: errorMessage });
      }
    }
  };

  return (
    <AuthShell headline={<>New password, twice.</>} sub="8 or more characters, with upper and lower case and a number.">
      {state === 'validating' && <p className="m-0 text-sm text-[#4A5468]">Checking your link…</p>}
      {(state === 'form' || state === 'submitting') && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <AuthTitle>Set a new password</AuthTitle>
          {errors.general && <AuthNote>{errors.general}</AuthNote>}
          <AuthField label="New password" name="password" type="password" value={formData.password} onChange={handleChange}
            placeholder="••••••••" error={errors.password} autoComplete="new-password" autoFocus disabled={loading} />
          <AuthField label="New password again" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange}
            placeholder="••••••••" error={errors.confirmPassword} autoComplete="new-password" disabled={loading} />
          <AuthSubmit loading={loading}>Save new password</AuthSubmit>
        </form>
      )}
      {state === 'success' && (
        <div className="flex flex-col gap-4">
          <span className="text-label uppercase text-[#14603F]">Done</span>
          <AuthTitle sub="Taking you to sign in with it…">Password changed</AuthTitle>
          <AuthSubmit type="button" onClick={() => navigate('/login', { replace: true })}>Sign in</AuthSubmit>
        </div>
      )}
      {state === 'invalid-token' && (
        <div className="flex flex-col gap-4">
          <span className="text-label uppercase text-[#B42318]">Link expired</span>
          <AuthTitle sub={errors.general || 'This reset link is missing, used or older than an hour.'}>Get a new link</AuthTitle>
          <AuthSubmit type="button" onClick={() => navigate('/forgot-password', { replace: true })}>Send a new one</AuthSubmit>
          <p className="m-0 text-center text-sm text-[#4A5468]"><Link to="/login" className={authLink}>Back to sign in</Link></p>
        </div>
      )}
    </AuthShell>
  );
};

export default ResetPasswordPage;
